import { Bot, RotateCcw, User } from "lucide-react";
import { Button } from "@/components/ui/Button";
import { Spinner } from "@/components/ui/Spinner";
import { useDocumentVersions, useRestoreVersion } from "../api/pdfApi";
import { humanSize } from "./DocumentStrip";
import type { PdfDocument } from "../types";

const fmt = (iso: string) =>
  new Date(iso).toLocaleString("pl-PL", { dateStyle: "short", timeStyle: "short" });

/**
 * Historia wersji = siatka bezpieczeństwa całego modułu. Każda operacja (kafelek,
 * asystent, edytor) dokłada wersję i nie rusza poprzedniej, więc „cofnij” zawsze
 * działa — także po tym, co zrobił asystent.
 */
export function VersionHistory({
  document: doc,
  sessionId,
}: {
  document: PdfDocument;
  sessionId: string;
}) {
  const { data: versions = [], isLoading } = useDocumentVersions(doc.id);
  const restore = useRestoreVersion();

  if (isLoading) {
    return (
      <div className="flex h-full items-center justify-center">
        <Spinner />
      </div>
    );
  }

  return (
    <div className="h-full overflow-y-auto p-3">
      <ol className="space-y-1.5">
        {versions.map((v) => {
          const current = v.number === doc.version_number;
          return (
            <li
              key={v.id}
              className={
                "flex items-start justify-between gap-3 border-l-[3px] bg-[var(--color-bg-surface)] px-3 py-2 " +
                (current
                  ? "border-l-[var(--color-primary)] border border-[var(--color-border-strong)]"
                  : "border-l-[var(--color-border-strong)] border border-[var(--color-border)]")
              }
            >
              <div className="min-w-0">
                <p className="flex items-center gap-1.5 text-xs text-[var(--color-text-primary)]">
                  <span className="font-mono font-bold">w{v.number}</span>
                  {v.source === "ai" ? (
                    <Bot size={12} className="text-[var(--color-primary)]" />
                  ) : (
                    <User size={12} className="text-[var(--color-text-muted)]" />
                  )}
                  <span className="truncate">{v.note || v.operation}</span>
                </p>
                <p className="mt-0.5 font-mono text-[10px] uppercase tracking-wide text-[var(--color-text-muted)]">
                  {fmt(v.created_at)} · {v.created_by_name || "—"} ·{" "}
                  {v.page_count ? `${v.page_count} str. · ` : ""}
                  {humanSize(v.size_bytes)}
                </p>
              </div>
              {current ? (
                <span className="section-label shrink-0 text-[var(--color-primary)]">bieżąca</span>
              ) : (
                <Button
                  type="button"
                  variant="ghost"
                  size="sm"
                  loading={restore.isPending}
                  onClick={() =>
                    restore.mutate({ documentId: doc.id, number: v.number, sessionId })
                  }
                  className="shrink-0"
                >
                  <RotateCcw size={12} className="mr-1" /> Przywróć
                </Button>
              )}
            </li>
          );
        })}
      </ol>
    </div>
  );
}
